import { AppBuilderStreamAPIName } from 'src/constants';
import { buildApiEndpointUrl, getInitForApi } from 'src/utils/AmplifyApiUtils';

import { ApiMethods, ApiTags, appAPI } from '.';

export type AppBuilderStatus =
  | 'generating'
  | 'ready'
  | 'deploying'
  | 'deployed'
  | 'installed'
  | 'failed';

export interface AppBuilder {
  id: string;
  object: 'appBuilder';
  name: string;
  description: string;
  icon: string;
  status: AppBuilderStatus;
  prompt: string;
  v0ProjectId: string;
  v0ChatId: string;
  v0LatestVersionId: string;
  v0DeploymentId: string;
  v0ChatUrl: string;
  v0DemoUrl: string;
  deploymentUrl: string;
  deployErrors?: string;
  installedAppId: string;
  installedModuleId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAppBuilderInput {
  name: string;
  description: string;
  prompt: string;
  icon: string;
  instructions?: string;
  branch?: string;
}

export interface SendAppBuilderMessageInput {
  id: string;
  message: string;
}

export type AppBuilderMessageActionHint =
  | 'resume'
  | 'open_in_v0'
  | 'send_followup'
  | 'skip'
  | 'allow';

/**
 * MessageBinaryFormat is the v0 SDK's structured AST format for rich content.
 * Each entry is a tuple: [type, data] where type 0 = markdown AST, type 1 = metadata.
 */

export type MessageBinaryFormat = [number, ...any[]][];

export interface AppBuilderMessage {
  id: string;
  content: string;
  experimentalContent?: MessageBinaryFormat;
  type: string;
  role: 'user' | 'assistant' | string;
  createdAt?: string;
  finishReason?: string;
  actionHints?: AppBuilderMessageActionHint[];
}

export interface AppBuilderMessagesResponse {
  data: AppBuilderMessage[];
}

interface SendMessageResponse {
  messageId?: string;
}

// Product agent types

export interface ProductAgentWizardContext {
  name: string;
  description: string;
  prompt: string;
  icon: string;
}

export interface ProductAgentMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ProductAgentQuestionOption {
  label: string;
  description: string;
}

export interface ProductAgentQuestion {
  question: string;
  header: string;
  options: ProductAgentQuestionOption[];
  multiSelect: boolean;
}

export interface ProductAgentChatInput {
  wizardContext: ProductAgentWizardContext;
  messages: ProductAgentMessage[];
}

export interface ProductAgentResponse {
  message: string;
  questions?: ProductAgentQuestion[];
  isComplete: boolean;
  finalizedPrompt?: string;
}

export class AppBuilderStreamError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'AppBuilderStreamError';
    this.status = status;
  }
}

const getStreamErrorMessage = async (response: Response) => {
  try {
    const payload = await response.json();
    if (
      payload &&
      typeof payload === 'object' &&
      'message' in payload &&
      typeof payload.message === 'string'
    ) {
      return payload.message;
    }
  } catch (error) {
    // Fall through to the generic message when the response is not JSON.
  }

  return 'Unable to stream message to app builder';
};

export const streamAppBuilderMessage = async ({
  id,
  message,
}: SendAppBuilderMessageInput) => {
  const requestInit = await getInitForApi(AppBuilderStreamAPIName, {
    method: 'POST',
    body: JSON.stringify({ message }),
    headers: {
      'Content-Type': 'application/json',
    },
  });

  const response = await fetch(
    buildApiEndpointUrl(
      AppBuilderStreamAPIName,
      `/app-builders/${id}/messages/stream`,
    ),
    requestInit,
  );

  if (!response.ok) {
    const errorMessage = await getStreamErrorMessage(response);
    throw new AppBuilderStreamError(errorMessage, response.status);
  }

  if (!response.body) {
    throw new AppBuilderStreamError(
      'App builder stream did not return a response body',
      response.status,
    );
  }

  return response.body;
};

export const getAppBuilderEntityTag = (id: string) => ({
  type: ApiTags.appBuilders as const,
  id,
});

export const getAppBuilderListTag = () => ({
  type: ApiTags.appBuilders as const,
  id: 'LIST',
});

export const getAppBuilderMessagesTag = (id: string) => ({
  type: ApiTags.appBuilders as const,
  id: `MESSAGES-${id}`,
});

export const appBuilderApi = appAPI.injectEndpoints({
  endpoints: (build) => ({
    createAppBuilder: build.mutation<AppBuilder, CreateAppBuilderInput>({
      query: (body) => ({
        path: '/app-builders',
        method: ApiMethods.post,
        options: { body },
      }),
      invalidatesTags: [getAppBuilderListTag()],
    }),
    listAppBuilders: build.query<AppBuilder[], void>({
      query: () => ({
        path: '/app-builders',
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: (result) => {
        const itemTags =
          result?.map((item) => getAppBuilderEntityTag(item.id)) ?? [];
        return [getAppBuilderListTag(), ...itemTags];
      },
    }),
    getAppBuilder: build.query<AppBuilder, { id: string }>({
      query: ({ id }) => ({
        path: `/app-builders/${id}`,
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: (_, __, arg) => [getAppBuilderEntityTag(arg.id)],
    }),
    sendAppBuilderMessage: build.mutation<
      SendMessageResponse,
      SendAppBuilderMessageInput
    >({
      query: ({ id, message }) => ({
        path: `/app-builders/${id}/messages`,
        method: ApiMethods.post,
        options: { body: { message } },
      }),
      invalidatesTags: (_, __, arg) => [getAppBuilderEntityTag(arg.id)],
    }),
    listAppBuilderMessages: build.query<
      AppBuilderMessagesResponse,
      {
        id: string;
        limit?: number;
        cursor?: string;
      }
    >({
      query: ({ id, limit, cursor }) => ({
        path: `/app-builders/${id}/messages`,
        method: ApiMethods.get,
        options: {
          queryStringParameters: {
            ...(limit ? { limit: String(limit) } : {}),
            ...(cursor ? { cursor } : {}),
          },
        },
      }),
      providesTags: (_, __, arg) => [getAppBuilderMessagesTag(arg.id)],
    }),
    resumeAppBuilderMessage: build.mutation<
      AppBuilderMessage,
      { id: string; messageId: string }
    >({
      query: ({ id, messageId }) => ({
        path: `/app-builders/${id}/messages/${messageId}/resume`,
        method: ApiMethods.post,
        options: {},
      }),
      invalidatesTags: (_, __, arg) => [getAppBuilderEntityTag(arg.id)],
    }),
    resolveAppBuilderTask: build.mutation<
      { success: boolean },
      { id: string; action: 'allow' | 'skip' }
    >({
      query: ({ id, action }) => ({
        path: `/app-builders/${id}/tasks/resolve`,
        method: ApiMethods.post,
        options: { body: { action } },
      }),
      invalidatesTags: (_, __, arg) => [getAppBuilderEntityTag(arg.id)],
    }),
    deployAppBuilder: build.mutation<AppBuilder, { id: string }>({
      query: ({ id }) => ({
        path: `/app-builders/${id}/deploy`,
        method: ApiMethods.post,
        options: {},
      }),
      invalidatesTags: (_, __, arg) => [
        getAppBuilderEntityTag(arg.id),
        getAppBuilderListTag(),
      ],
    }),
    deleteAppBuilder: build.mutation<{ success: boolean }, { id: string }>({
      query: ({ id }) => ({
        path: `/app-builders/${id}`,
        method: ApiMethods.del,
        options: {},
      }),
      invalidatesTags: [ApiTags.appBuilders],
    }),
    sendProductAgentMessage: build.mutation<
      ProductAgentResponse,
      ProductAgentChatInput
    >({
      query: (body) => ({
        path: '/app-builders/product-agent/chat',
        method: ApiMethods.post,
        options: { body },
      }),
    }),
  }),
});

export const {
  useCreateAppBuilderMutation,
  useListAppBuildersQuery,
  useGetAppBuilderQuery,
  useSendAppBuilderMessageMutation,
  useListAppBuilderMessagesQuery,
  useResumeAppBuilderMessageMutation,
  useResolveAppBuilderTaskMutation,
  useDeployAppBuilderMutation,
  useDeleteAppBuilderMutation,
  useSendProductAgentMessageMutation,
} = appBuilderApi;
